import React from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { Dismiss20Regular, ChevronDown20Regular } from '@fluentui/react-icons';

export interface BulkAction {
  key: string;
  label: string;
  icon?: React.ReactElement;
  onClick: () => void;
  /** Disable the action (e.g. nothing in the selection is eligible). */
  disabled?: boolean;
}

interface BulkActionBarProps {
  /** Number of selected rows. The bar renders nothing when this is 0. */
  selectedCount: number;
  /** Clear the current selection. */
  onClear: () => void;
  /** Bulk actions to offer. One action renders inline; multiple collapse
   *  into a single "Actions" menu so the host row stays uncluttered. */
  actions: BulkAction[];
  /** Noun for the count label, e.g. {singular:'order', plural:'orders'}
   *  → "3 orders selected". Defaults to a generic "item"/"items". */
  noun?: { singular: string; plural: string };
}

const useStyles = makeStyles({
  // Right-aligns within the host row (the table search/filter bar) so the
  // search input keeps its place and the bar fills the trailing space —
  // the row already reserves its height, so toggling selection never
  // shifts the page layout.
  root: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    marginLeft: 'auto',
    flexShrink: 0,
  },
  count: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
});

/**
 * Selection action cluster for table views. Shows "N selected", a clear
 * button, and one-or-more bulk actions. Designed to sit on the trailing
 * edge of a page's table search/filter row.
 */
const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onClear,
  actions,
  noun = { singular: 'item', plural: 'items' },
}) => {
  const styles = useStyles();

  if (selectedCount <= 0) return null;

  const label = `${selectedCount} ${selectedCount === 1 ? noun.singular : noun.plural} selected`;

  return (
    <div className={styles.root}>
      <Button
        appearance="subtle"
        icon={<Dismiss20Regular />}
        onClick={onClear}
        aria-label="Clear selection"
      />
      <span className={styles.count}>{label}</span>
      {actions.length === 1 ? (
        <Button
          appearance="secondary"
          icon={actions[0].icon}
          onClick={actions[0].onClick}
          disabled={actions[0].disabled}
        >
          {actions[0].label}
        </Button>
      ) : actions.length > 1 ? (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button appearance="secondary" iconPosition="after" icon={<ChevronDown20Regular />}>
              Actions
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {actions.map((action) => (
                <MenuItem
                  key={action.key}
                  icon={action.icon}
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  {action.label}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      ) : null}
    </div>
  );
};

export default BulkActionBar;
